import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import { categories } from '@/data/categories';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.slug === slug);

  const articleSchema = useMemo(() => {
    if (!article) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.imageUrl,
      author: {
        '@type': 'Organization',
        name: 'YOYBUY Spreadsheet',
        url: `${siteUrl}/`,
      },
      publisher: {
        '@type': 'Organization',
        name: 'YOYBUY Spreadsheet',
        url: `${siteUrl}/`,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
        },
      },
      datePublished: '2026-07-05',
      dateModified: '2026-07-05',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/guide/${article.slug}`,
      },
      inLanguage: 'ru',
    };
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
            <i className="ri-search-line text-accent-500 text-2xl w-8 h-8 flex items-center justify-center"></i>
          </div>
          <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
            {t('articlePage.notFound')}
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            {t('articlePage.notFoundDesc')}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            {t('articlePage.backHome')}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      {articleSchema && <JsonLd data={articleSchema as unknown as Record<string, unknown>} />}
      <nav className="sticky top-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/40">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-1.5 text-foreground-800 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
              <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium whitespace-nowrap hidden sm:inline">{t('articlePage.backHome')}</span>
            </Link>
            <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors duration-200 cursor-pointer whitespace-nowrap hidden sm:inline">
              {t('nav.home')}
            </Link>
            <Link to="/#articles-section" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors duration-200 cursor-pointer whitespace-nowrap hidden sm:inline">
              {t('nav.shoppingGuide')}
            </Link>
          </div>
          <Link to="/" className="text-lg font-heading font-bold text-foreground-900 cursor-pointer whitespace-nowrap">
            {t('site.title')}
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <Link to="/" className="text-xs px-3 py-1 rounded-full bg-accent-100/80 text-accent-700 hover:bg-accent-200/80 transition-colors duration-200 cursor-pointer whitespace-nowrap">
              {t('site.title')}
            </Link>
            <span className="text-xs text-foreground-400">{article.meta}</span>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-5 leading-tight">
            {article.title}
          </h1>

          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full rounded-2xl object-cover aspect-[2/1] mb-8"
          />
        </header>

        <div className="prose-custom">
          <section className="mb-10">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              {t('articlePage.intro')}
            </p>
          </section>

          {article.sections.map((section, idx) => {
            if (section.type === 'text') {
              return (
                <section key={idx} className="mb-10">
                  {section.title && (
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                      {section.title}
                    </h2>
                  )}
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {section.content}
                  </p>
                  {section.paragraph2 && (
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {section.paragraph2}
                    </p>
                  )}
                </section>
              );
            }

            if (section.type === 'features') {
              return (
                <section key={idx} className="mb-10 bg-accent-50/50 rounded-2xl p-6 md:p-8 border border-accent-200/40">
                  {section.title && (
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-5">
                      {section.title}
                    </h2>
                  )}
                  <ul className="space-y-3">
                    {section.items?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                        <i className="ri-checkbox-circle-fill text-accent-500 w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            }

            if (section.type === 'cta') {
              return (
                <section key={idx} className="mb-12">
                  {section.title && (
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                      {section.title}
                    </h2>
                  )}
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                    {section.content}
                  </p>
                  {section.ctaUrl && section.ctaLabel && (
                    <a
                      href={section.ctaUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-external-link-line text-lg w-5 h-5 flex items-center justify-center"></i>
                      {section.ctaLabel}
                    </a>
                  )}
                  {section.relatedTitle && (
                    <div className="mt-8 pt-8 border-t border-background-200/60">
                      <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                        {section.relatedTitle}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((cat) => (
                          <a
                            key={cat.id}
                            href={cat.url}
                            target="_blank"
                            rel="noopener nofollow"
                            className="text-xs px-3 py-1.5 rounded-full bg-background-100 border border-background-200/60 text-foreground-600 hover:bg-accent-100 hover:text-accent-700 hover:border-accent-300/60 transition-all duration-200 cursor-pointer whitespace-nowrap"
                          >
                            {t(cat.nameKey)}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              );
            }

            return null;
          })}
        </div>

        <div className="mt-10 pt-8 border-t border-background-200/60">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              {t('articlePage.backHome')}
            </Link>

            <div className="flex flex-wrap items-center gap-3">
              {articles.filter(a => a.slug !== slug).slice(0, 3).map((a) => (
                <Link
                  key={a.slug}
                  to={`/guide/${a.slug}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-background-100 border border-background-200/60 text-foreground-600 hover:bg-accent-100 hover:text-accent-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {a.title.length > 35 ? a.title.slice(0, 35) + '...' : a.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <footer className="w-full bg-accent-50 border-t border-background-200/60 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Link to="/" className="text-sm font-heading font-semibold text-foreground-900 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
            {t('site.title')}
          </Link>
          <p className="text-xs text-foreground-400 mt-2">
            © 2026 YOYBUY Spreadsheet. Все права защищены.
          </p>
        </div>
      </footer>
    </main>
  );
}